import { ReactElement, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, LinearProgress, Stack, Typography } from '@mui/material';
import { ctxPopup } from '@/contexts/PopupContext';
import { provideTachographIcon } from '@/components/common/IconProvider';
import { TachographDataDuringFromToPeriod } from './TachographService';
import { TachographDiagramDateTitle } from './TachographDiagramDateTitle';
import { TachographDiagram } from './TachographDiagram';

export type FetchStatus = 'idle' | 'loading' | 'empty' | 'data';

export function DataView({
  tachographProcessedData,
}: {
  tachographProcessedData: TachographDataDuringFromToPeriod[] | undefined;
}) {
  const popupCtx = useContext(ctxPopup);
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  function calculateWidth() {
    return `calc(100vw - ${popupCtx?.drawerMenuOpen ? 750 : 568}px)`;
  }
  const [width, setWidth] = useState(calculateWidth());

  useEffect(() => {
    setTimeout(() => {
      setWidth(calculateWidth());
    }, 300);
  }, [popupCtx?.drawerMenuOpen]);

  return (
    <Box flex="1" overflow="auto" bgcolor="grey.100" px={2}>
      {tachographProcessedData?.map((data, index) => {
        if (data.states) {
          const isSelected = selectedIndex === index;
          return (
            <Box
              bgcolor={'#fff'}
              borderRadius={2}
              my={3}
              p={1}
              border={2}
              width={width}
              borderColor={isSelected ? 'primary.main' : 'grey.300'}
              position={'relative'}
              onClick={() => setSelectedIndex(isSelected ? -1 : index)}
            >
              <TachographDiagramDateTitle
                from={tachographProcessedData[index].fromDate}
                to={tachographProcessedData[index].fromDate}
              />
              <TachographDiagram
                data={tachographProcessedData[index]}
                key={tachographProcessedData[index].fromDate.toString()}
                index={index}
                selectedIndex={selectedIndex}
              />
            </Box>
          );
        } else
          return (
            <Box display="flex" alignItems="center" alignContent="center" flexWrap="unset" my={3}>
              <TachographDiagramDateTitle from={data.fromDate} to={data.toDate} />
              <Box flex="1">
                <Divider textAlign="center">
                  <Box flexDirection="column" flex="1" alignItems="center">
                    <Box component="img" src={provideTachographIcon('data')} />
                    <Typography variant="subtitle1">{t('tachographDiagram.noDataForThisPeriod')}</Typography>
                  </Box>
                </Divider>
              </Box>
            </Box>
          );
      })}
    </Box>
  );
}

export function DataMobileView({
  tachographProcessedData,
}: {
  tachographProcessedData: TachographDataDuringFromToPeriod[] | undefined;
}) {
  const { t } = useTranslation();
  return (
    <Box sx={{ flex: 1, overflow: 'auto', backgroundColor: 'grey.200' }}>
      {tachographProcessedData?.map((data, index) => {
        if (data.states) {
          return (
            <Box sx={{ mt: 2, position: 'relative', backgroundColor: '#fff' }}>
              <Box sx={{ pt: 1, pl: 1 }}>
                <TachographDiagramDateTitle
                  from={tachographProcessedData[index].fromDate}
                  to={tachographProcessedData[index].fromDate}
                />
              </Box>
              <TachographDiagram
                data={tachographProcessedData[index]}
                key={tachographProcessedData[index].fromDate.toString()}
                index={index}
              />
            </Box>
          );
        } else {
          return (
            <Stack my={2} spacing={1}>
              <TachographDiagramDateTitle from={data.fromDate} to={data.toDate} />
              <Box flex="1" textAlign="center">
                <Box flexDirection="column" flex="1" alignItems="center">
                  <Box component="img" src={provideTachographIcon('data')} />
                  <Typography variant="subtitle1">{t('tachographDiagram.noDataForThisPeriod')}</Typography>
                </Box>
              </Box>
            </Stack>
          );
        }
      })}
    </Box>
  );
}

export function LoadingView() {
  const { t } = useTranslation();
  const idleIcon = <Box component="img" src={provideTachographIcon('idle')} />;
  return (
    <Box flex={1} display="flex" flexDirection="column" height="100%">
      <LinearProgress sx={{ height: 6 }} />
      <EmptyStateFullScreen icon={idleIcon} title={t('tachographDiagram.idleTitle') + '...'} />
    </Box>
  );
}

export function IdleView() {
  const { t } = useTranslation();
  const idleIcon = <Box component="img" src={provideTachographIcon('idle')} />;
  return (
    <EmptyStateFullScreen
      icon={idleIcon}
      title={t('tachographDiagram.idleTitle') + '!'}
      description={t('tachographDiagram.diagramExplanation')}
    />
  );
}
export function EmptyView() {
  const { t } = useTranslation();
  const emptyIcon = <Box component="img" src={provideTachographIcon('empty')} />;
  return (
    <EmptyStateFullScreen
      icon={emptyIcon}
      title={t('tachographDiagram.emptyTitle')}
      description={t('tachographDiagram.emptyDescription')}
    />
  );
}

function EmptyStateFullScreen({
  icon,
  title,
  description,
}: {
  icon: ReactElement;
  title: string;
  description?: string;
}) {
  return (
    <Stack
      spacing={6}
      flex={1}
      alignItems="center"
      justifyContent="center"
      whiteSpace="pre-line"
      border={1}
      borderColor="grey.400"
      borderRadius={2}
      m={2}
      p={4}
    >
      {icon}
      <Typography variant="h6" align="center">
        {title}
      </Typography>
      {description && (
        <Typography variant="subtitle1" align="center" pb={6}>
          {description}
        </Typography>
      )}
    </Stack>
  );
}
